import type { CSSProperties } from 'react';
import {
    Key,
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import { createPortal } from 'react-dom';

import dotsTree from '@/assets/icons/dots-tree.svg';

import classes from './TableMemberManagement.module.css';

const MENU_CLOSE_DELAY_MS = 150;
/** Above management modal (.container z-index 99) and sticky table header. */
const MENU_PORTAL_Z_INDEX = 10_000;

export type optionTypes = {
    icon: string;
    label: string;
    onClick: (e) => void;
    userPermissions: string[];
};
type IProps = {
    data;
    onMouseEnter;
    onMouseLeave;
    canMap: boolean;
    actionIconsPermissions: optionTypes[];
};

const HoverIcons: React.FC<IProps> = props => {
    const { data, onMouseEnter, onMouseLeave, canMap, actionIconsPermissions } =
        props;

    const containerRef = useRef<HTMLDivElement>(null);
    const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [menuPosition, setMenuPosition] = useState<CSSProperties | null>(
        null,
    );

    const clearCloseTimer = () => {
        if (closeTimerRef.current) {
            clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
    };

    const scheduleClose = () => {
        clearCloseTimer();
        closeTimerRef.current = setTimeout(() => {
            onMouseLeave();
        }, MENU_CLOSE_DELAY_MS);
    };

    const computeMenuPosition = useCallback((): CSSProperties | null => {
        const el = containerRef.current;
        if (!el) {
            return null;
        }
        const rect = el.getBoundingClientRect();
        return {
            position: 'fixed',
            right: window.innerWidth - rect.right,
            bottom: window.innerHeight - rect.bottom,
            transform: 'translateX(-20px)',
            zIndex: MENU_PORTAL_Z_INDEX,
        };
    }, []);

    useLayoutEffect(() => {
        if (!canMap) {
            setMenuPosition(null);
            return;
        }
        setMenuPosition(computeMenuPosition());
    }, [canMap, computeMenuPosition, data?.id]);

    useEffect(() => {
        if (!canMap) {
            return;
        }
        const onScrollOrResize = () => {
            setMenuPosition(computeMenuPosition());
        };
        window.addEventListener('scroll', onScrollOrResize, true);
        window.addEventListener('resize', onScrollOrResize);
        return () => {
            window.removeEventListener('scroll', onScrollOrResize, true);
            window.removeEventListener('resize', onScrollOrResize);
        };
    }, [canMap, computeMenuPosition]);

    useEffect(() => {
        return () => clearCloseTimer();
    }, []);

    const menuPortal =
        canMap &&
        menuPosition &&
        typeof document !== 'undefined' &&
        createPortal(
            <div
                className={classes['edit-image-popup-container']}
                style={menuPosition}
                onMouseEnter={clearCloseTimer}
                onMouseLeave={scheduleClose}>
                <div className={classes['edit-image-popup']}>
                    <div className={classes['triangle']}></div>
                    {actionIconsPermissions.map((item, index: Key) => {
                        return (
                            <div
                                key={index}
                                onClick={() => item.onClick(data)}
                                className={classes['edit-image-popup-row']}>
                                <img
                                    alt="action"
                                    height={18}
                                    width={18}
                                    src={item?.icon}
                                />
                                <p>{item?.label}</p>
                            </div>
                        );
                    })}
                </div>
            </div>,
            document.body,
        );

    return (
        <div
            ref={containerRef}
            className={classes['tree-dots-container']}
            onMouseEnter={() => {
                clearCloseTimer();
                onMouseEnter();
            }}
            onMouseLeave={scheduleClose}>
            <img alt="options" style={{ cursor: 'pointer' }} src={dotsTree} />
            {menuPortal}
        </div>
    );
};
export default HoverIcons;
